import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { CustomVizRequest } from 'libs/end-to-end/src/custom-viz/CustomVizRequest';
import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { Setter } from 'libs/end-to-end/src/ui-types';
import { UserAuthenticatedApiCall } from 'apps/sigma2-ui/src/state/user-types';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { useAnalytics } from 'apps/sigma2-ui/src/lib/hooks/useAnalytics';
import { mapCustomVizRequestToAnalyticsEventProperties } from 'apps/sigma2-ui/src/components/custom-viz/CustomVizHelpers';
import { CustomVizStartedAnalyticsEvent } from 'libs/end-to-end/src/analytics/CustomVizStartedAnalyticsEvent';
import { copyObjectWithKeysSorted } from 'libs/end-to-end/src/copy-object-with-keys-sorted';
import { CustomVizStartResponse } from 'libs/end-to-end/src/custom-viz/CustomVizDefinitions';
import { jsonParse } from 'libs/end-to-end/src/utils/utility/json-parse';
import { CustomVizDefinition } from 'libs/end-to-end/src/custom-viz-definition';
import { waitForMs } from 'libs/end-to-end/src/generic';

export const doCustomVizsEquate = (
  a: CustomVizRequest | null,
  b: CustomVizRequest | null,
): boolean => {
  if (a === null && b === null) {
    return false;
  }

  if (a === null || b === null) {
    return false;
  }

  const sortedCustomVizA = copyObjectWithKeysSorted(a);
  const sortedCustomVizB = copyObjectWithKeysSorted(b);

  return JSON.stringify(sortedCustomVizA) === JSON.stringify(sortedCustomVizB);
};

export const useCustomVizDoViz = ({
  userAuthenticatedApiCall,
  setVizInProgressExecuting,
  beforeRequestStarted,
  afterRequestStarted,
  onStaleResultLoaded,
  getVizInProgressExecuting,
  onError,
  customVizDefinition,
}: {
  userAuthenticatedApiCall: UserAuthenticatedApiCall;
  setVizInProgressExecuting: Setter<CustomVizRequest | null>;
  beforeRequestStarted: () => void;
  afterRequestStarted: (params: {
    startingTookMultipleAttempts: boolean;
  }) => void;
  onStaleResultLoaded: (params: {
    staleResultForRequest: unknown;
    staleResultCreatedAtForRequest: number | null;
    currentRequest: CustomVizRequest;
  }) => void;
  onError: (error: BaseError) => void;
  getVizInProgressExecuting: () => CustomVizRequest | null;
  customVizDefinition: CustomVizDefinition;
}) => {
  const analytics = useAnalytics();

  return useAsyncCallback(
    // eslint-disable-next-line complexity
    async (isAlive, request: CustomVizRequest) => {
      if (!isAlive()) {
        return;
      }

      const eventProperties = mapCustomVizRequestToAnalyticsEventProperties({
        request,
      });

      const event: CustomVizStartedAnalyticsEvent = {
        eventName: 'Custom Viz Started',
        eventProperties,
      };

      try {
        analytics.trackEvent(event);
      } catch (e) {
        console.error('Failed to track analytics event', e);
      }

      beforeRequestStarted();
      setVizInProgressExecuting(request);

      let startingTookMultipleAttempts = false;

      while (isAlive() && getVizInProgressExecuting() === request) {
        const startVizResult = await userAuthenticatedApiCall({
          responseSchema: CustomVizStartResponse,
          url: `${getApiBaseUrl()}/v2/custom-viz/viz`,
          requestOptions: {
            method: 'POST',
            body: JSON.stringify(request),
          },
        });

        if (!isAlive() || !startVizResult) {
          return;
        }

        if (startVizResult.resultType === 'err') {
          setVizInProgressExecuting(null);
          onError(startVizResult.error);
          return;
        }

        // If we have a stale result to peek, let's use it now. We don't break control flow here.
        if (startVizResult.value.staleOutputContents !== null) {
          const jsonParseStaleResult = jsonParse(
            startVizResult.value.staleOutputContents,
            customVizDefinition.outputSchema,
          );

          if (jsonParseStaleResult.resultType === 'ok') {
            onStaleResultLoaded({
              staleResultForRequest: jsonParseStaleResult.value,
              staleResultCreatedAtForRequest:
                startVizResult.value.staleCreatedAt,
              currentRequest: request,
            });
          }
        }

        if (!startVizResult.value.started) {
          startingTookMultipleAttempts = true;
          await waitForMs(1000);
          continue;
        }

        // We started the request, now just ensure things match.
        break;
      }

      if (!isAlive() || getVizInProgressExecuting() !== request) {
        return;
      }

      afterRequestStarted({ startingTookMultipleAttempts });
    },
    [
      analytics,
      setVizInProgressExecuting,
      getVizInProgressExecuting,
      userAuthenticatedApiCall,
      beforeRequestStarted,
      afterRequestStarted,
      customVizDefinition,
      onStaleResultLoaded,
      onError,
    ],
  );
};
